import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ErrorCode,
  type FinanceSettingsDto,
  type SaveFinanceSettingsRequest,
  type ComplianceProfileDto,
} from '@signalvote/shared';
import { skip, type Payloads, type WriteResult } from '@signalvote/data';
import type { FinanceSettingsRead } from '../../lib/financeSettingsRead';
import type { ComplianceProfileRead } from '../../lib/complianceProfileRead';
import { apiJson, apiResponse } from './apiFetch';
import { financeQueries } from '../../lib/queryKeys';
import { campaignHeaders, defineQuery, defineWrite, edit } from '../../lib/data';
import { useCampaignStore } from '../../stores';
import { FINANCE_STALE_TIME } from './queryUtils';

// Re-export types for consumer convenience
export type { FinanceSettingsDto, SaveFinanceSettingsRequest, ComplianceProfileDto };

/** The campaign's finance settings; null until the committee has saved any. */
export const financeSettingsRead = defineQuery({
  key: () => ['finance', 'settings'],
  reads: 'GET /api/finance/settings',
  staleTime: FINANCE_STALE_TIME,
  fetch: async (_: undefined, { campaignId }): Promise<FinanceSettingsDto | null> => {
    const response = await apiResponse('/api/finance/settings', { headers: campaignHeaders(campaignId) });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to fetch finance settings');
    return response.json() as Promise<FinanceSettingsDto>;
  },
});

/** The settings, idle for a user without Finance Manage: the GET would only collect a 403. */
export function useFinanceSettings(): FinanceSettingsRead {
  const canManageFinance = useCampaignStore((s) => s.canManage('Finance'));
  return financeSettingsRead(canManageFinance ? undefined : skip);
}

/** The finance reads no write set reaches yet. A declared read already refetched from the write set; a second invalidation would fetch it again. */
export const undeclaredFinanceQueries = {
  predicate: (query: { queryKey: readonly unknown[]; meta?: Record<string, unknown> }) =>
    financeQueries.predicate(query) && !query.meta?.reads,
};

/**
 * A write that can change any finance figure (a settings save, an import, a bank pull) still
 * refreshes the finance reads that are not defined reads yet (reports, loans, audit log, …): no
 * write set reaches them. The defined finance reads refresh from the write set alone.
 */
export function useRefreshFinanceAfter<I, R, Pl extends Payloads>(write: WriteResult<I, R, Pl>): WriteResult<I, R, Pl> {
  const queryClient = useQueryClient();
  const { mutateAsync: send } = write;
  const mutateAsync = useCallback<WriteResult<I, R, Pl>['mutateAsync']>(
    async (input, options) => {
      const result = await send(input, options);
      void queryClient.invalidateQueries(undeclaredFinanceQueries);
      return result;
    },
    [send, queryClient],
  );
  const mutate = useCallback<WriteResult<I, R, Pl>['mutate']>(
    (input, options) => {
      mutateAsync(input, options).catch(() => undefined);
    },
    [mutateAsync],
  );
  return { ...write, mutate, mutateAsync };
}

/**
 * The jurisdiction's rules for this campaign; null when no ruleset is assigned. It reads the finance
 * settings, so a settings write refreshes it through the write set.
 */
export const complianceProfileRead = defineQuery({
  key: () => ['finance', 'compliance-profile'],
  reads: 'GET /api/finance/compliance-profile',
  staleTime: FINANCE_STALE_TIME,
  fetch: async (_: undefined, { campaignId }): Promise<ComplianceProfileDto | null> => {
    const response = await apiResponse('/api/finance/compliance-profile', { headers: campaignHeaders(campaignId) });
    if (response.status === 404) return null;
    if (!response.ok) throw new Error('Failed to fetch compliance profile');
    return response.json() as Promise<ComplianceProfileDto>;
  },
});

export function useComplianceProfile(): ComplianceProfileRead {
  return complianceProfileRead(undefined);
}

const saveFinanceSettings = defineWrite({
  send: (data: SaveFinanceSettingsRequest) =>
    apiJson<FinanceSettingsDto>('/api/finance/settings', {
      method: 'POST',
      json: data,
      error: (body, response) =>
        response.status === 409
          ? Object.assign(
              new Error('These settings were updated elsewhere. Your changes are still here; save again to apply them.'),
              { status: 409, code: ErrorCode.ConcurrencyConflict },
            )
          : Object.assign(new Error(body?.error || 'Failed to save finance settings'), { status: response.status, code: body?.code }),
    }),
  // Shown on click so a stale save restores the settings and refetches the server's token.
  patch: ({ concurrencyVersion: _sent, ...fields }) => [
    edit(financeSettingsRead, undefined, (settings) => settings && { ...settings, ...fields }),
  ],
  // The form re-initialises from this read right after a save, so it must hold the fresh token
  // before any refetch lands, or the next save is a false 409.
  land: (saved) => [edit(financeSettingsRead, undefined, () => saved)],
});

export function useSaveFinanceSettings() {
  return useRefreshFinanceAfter(saveFinanceSettings());
}
